import { useEffect, useState } from 'react';
import { api } from '../../api/client';
import type { Personne, Pole } from '../../types';

// Modale de création/édition d'une personne. Partagée par RemplacantsPage.
// - Création = un·e CANDIDAT·E du vivier externe (pas encore dans Eveil) :
//   les internes, externes enregistré·e·s et stagiaires arrivent d'Eveil V2.1.
// - Édition : l'identité (nom, e-mail, téléphone) d'une personne venue d'Eveil
//   est affichée en lecture seule (elle se corrige dans le CRM) ; on ne modifie
//   ici que les données du portail — pôles déclarés, notes, lien Tipee.
export function PersonneModal({ poles, personne, onClose, onSaved }: {
  poles: Pole[];
  personne: Personne | null;      // null = nouveau·elle candidat·e du vivier
  defaultExterne?: boolean;
  onClose: () => void;
  onSaved: (p: Personne) => void;
}) {
  const identiteModifiable = personne == null || personne.origine === 'candidat';
  const [prenom, setPrenom] = useState(personne?.prenom ?? '');
  const [nom, setNom] = useState(personne?.nom ?? '');
  const [email, setEmail] = useState(personne?.email ?? '');
  const [telephone, setTelephone] = useState(personne?.telephone ?? '');
  // Seuls les pôles DÉCLARÉS se modifient ; ceux déduits des ateliers animés
  // (Eveil) restent affichés à part.
  const [poleIds, setPoleIds] = useState<number[]>(personne?.pole_ids_declares ?? []);
  const polesDeduits = (personne?.pole_ids ?? []).filter((id) => !(personne?.pole_ids_declares ?? []).includes(id));
  const [notes, setNotes] = useState(personne?.notes ?? '');
  const [tipeeId, setTipeeId] = useState(personne?.tipee_resource_id ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const surEchap = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', surEchap);
    return () => document.removeEventListener('keydown', surEchap);
  }, [onClose]);

  const togglePole = (id: number) =>
    setPoleIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const submit = async () => {
    if (identiteModifiable && !nom.trim()) { setError('Le nom est requis.'); return; }
    setSaving(true);
    setError(null);
    const payload: Partial<Personne> = {
      notes: notes.trim() || null,
      pole_ids: poleIds,
      ...(identiteModifiable ? {
        prenom: prenom.trim(),
        nom: nom.trim(),
        email: email.trim() || null,
        telephone: telephone.trim() || null,
      } : {}),
      ...(personne && personne.origine === 'interne' ? { tipee_resource_id: tipeeId.trim() || null } : {}),
    };
    try {
      const saved = personne
        ? await api.updatePersonne(personne.id, payload)
        : await api.createPersonne({ ...payload, statut_externe: 'candidat' });
      onSaved(saved);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Échec de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const ORIGINES: Record<Personne['origine'], string> = {
    interne: 'Collaborateur·rice (Eveil)',
    externe: 'Intervenant·e externe (Eveil)',
    stagiaire: 'Stagiaire (Eveil)',
    candidat: 'Candidat·e du vivier (pas encore dans Eveil)',
  };

  return (
    <div className="modal-overlay">
      <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">{personne ? personne.display_name : 'Ajouter un·e candidat·e au vivier'}</h3>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {error && <div className="badge badge-err mb-12" style={{ display: 'block' }}>{error}</div>}
          {personne && <div className="text-xs text-ink-3 mb-12">{ORIGINES[personne.origine]}</div>}
          {!personne && (
            <p className="text-sm text-muted mb-12">
              Les collaborateur·rice·s et les externes déjà enregistré·e·s viennent d'Eveil. Ajoutez ici une
              personne qui n'y est pas encore : dès qu'elle sera enregistrée dans le CRM (même e-mail), sa fiche
              Eveil remplacera ces coordonnées.
            </p>
          )}
          {identiteModifiable ? (
            <>
              <div className="flex gap-12">
                <div className="form-group flex-1">
                  <label className="form-label">Prénom</label>
                  <input className="form-input" value={prenom} onChange={(e) => setPrenom(e.target.value)} />
                </div>
                <div className="form-group flex-1">
                  <label className="form-label">Nom *</label>
                  <input className="form-input" value={nom} onChange={(e) => setNom(e.target.value)} />
                </div>
              </div>
              <div className="flex gap-12">
                <div className="form-group flex-1">
                  <label className="form-label">E-mail</label>
                  <input className="form-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
                </div>
                <div className="form-group flex-1">
                  <label className="form-label">Téléphone</label>
                  <input className="form-input" value={telephone} onChange={(e) => setTelephone(e.target.value)} />
                </div>
              </div>
            </>
          ) : (
            <div className="card mb-12">
              <div className="text-sm">{personne!.email ?? 'Pas d’e-mail'} · {personne!.telephone ?? 'Pas de téléphone'}</div>
              <div className="text-xs text-ink-3 mt-4">Nom, e-mail et téléphone viennent d'Eveil : corrigez-les dans le CRM.</div>
            </div>
          )}
          {personne?.origine === 'interne' && (
            <div className="form-group">
              <label className="form-label">Identifiant Tipee</label>
              <div className="text-xs text-ink-3 mb-4">Relié automatiquement (e-mail ou nom) ; à saisir seulement si le rapprochement a échoué.</div>
              <input className="form-input" style={{ maxWidth: 260 }} value={tipeeId} onChange={(e) => setTipeeId(e.target.value)} />
            </div>
          )}
          <div className="form-group">
            <label className="form-label">Pôles reconnus</label>
            {polesDeduits.length > 0 && (
              <div className="text-xs text-ink-3 mb-4">
                Déjà déduits des ateliers animés : {polesDeduits.map((id) => poles.find((p) => p.id === id)?.nom).filter(Boolean).join(', ')}
              </div>
            )}
            <div className="selectable-group">
              {poles.map((p) => (
                <button type="button" key={p.id}
                  className={`selectable ${poleIds.includes(p.id) ? 'active' : ''}`}
                  onClick={() => togglePole(p.id)}>
                  {p.nom}
                </button>
              ))}
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Notes</label>
            <textarea className="form-textarea" value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn" onClick={onClose}>Annuler</button>
          <button className="btn btn-accent" onClick={submit} disabled={saving}>
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
}
